import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Layers, Loader2, Ruler, Scissors, Upload } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Crumbs } from "@/components/layout/Crumbs";
import { Reveal } from "@/components/Reveal";
import api, { mediaUrl } from "@/lib/api";
import { useMall } from "@/context/MallContext";
import { useShop } from "@/lib/useShop";
import { PickShopGate } from "@/components/mall/PickShopGate";
import { themeOf } from "@/lib/mall";
import { StoreButton } from "@/components/Buttons";
import { DTF_PAGE } from "@/lib/nav";
import { PageBanner } from "@/components/mall/PageBanner";

const STEPS = [
    { icon: Upload, title: "Upload your designs", text: "PNG, JPG, SVG or PDF. As many as you like, at 300 dpi for a crisp press." },
    { icon: Layers, title: "We nest them for you", text: "Every image lands on the sheet with a half-inch gap. Drag, rotate or resize anything." },
    { icon: Scissors, title: "Press them on anything", text: "Cut each transfer out and heat-press it on a tee, hoodie, tote or hat." },
];

/** The DTF gang sheet, on its own page: what it is, what it costs, and the door into the builder. */
export default function DtfPage() {
    const { store: activeStore } = useMall();
    const { shopSlug } = useShop();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!shopSlug) return;
        api.get("/catalog/shirts", { params: { store: shopSlug } }).then(({ data }) => setData(data.dtf))
            .catch(() => setData(null)).finally(() => setLoading(false));
    }, [shopSlug]);

    if (!shopSlug) return <Layout><PickShopGate room="dtf" title="Which shop prints your gang sheet?" to={DTF_PAGE.to} /></Layout>;
    if (loading) return <Layout><div className="flex min-h-[60vh] items-center justify-center text-zinc-400"><Loader2 className="animate-spin" /></div></Layout>;

    const theme = themeOf(activeStore);
    const cfg = data?.config || {};
    const q = `?store=${shopSlug}`;
    const specs = [
        ["Film width", `${cfg.width_inches || 16.5}"`], ["Printable", `${cfg.printable_width_inches || 15.5}" wide`],
        ["Price", `$${Number(cfg.price_per_linear_foot || 5).toFixed(2)} a foot`], ["Spacing", `${cfg.min_spacing_inches || 0.5}" between images`],
        ["Length", `${(cfg.min_length_inches || 12) / 12} ft to ${(cfg.max_length_inches || 600) / 12} ft`], ["Ready in", data?.ready_label || "Same Day"],
    ];

    return (
        <Layout>
            <PageBanner room="dtf" />
            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="dtf-page">
                <Reveal>
                    <Crumbs trail={[{ label: "Shirts", to: "/shirts" }, { label: DTF_PAGE.label }]}
                        pills={[{ key: "shirts", label: "All shirts", to: "/shirts" }, { key: "dtf", label: DTF_PAGE.label, to: DTF_PAGE.to, active: true }]} />
                </Reveal>
                {!data ? (
                    <div className="mt-6 rounded-2xl border border-dashed border-zinc-200 bg-white p-10 text-center" data-testid="dtf-closed">
                        <p className="text-base font-semibold text-zinc-900">{activeStore?.name || "This shop"} does not run a gang sheet counter.</p>
                        <Link to="/shirts" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold underline">Back to Shirts <ArrowRight size={14} /></Link>
                    </div>
                ) : (
                    <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_440px]">
                        <Reveal>
                            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Transfers · {data.ready_label}{activeStore ? ` · printed by ${activeStore.name}` : ""}</p>
                            <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl" data-testid="dtf-title">DTF Gang Sheet</h1>
                            <p className="mt-3 max-w-xl text-base text-zinc-600">
                                One sheet, all your designs. You pay by the foot, not by the image — so the more you fit on it, the cheaper every transfer gets.
                            </p>
                            <dl className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="dtf-specs">
                                {specs.map(([k, v]) => (
                                    <div key={k} className="rounded-xl border border-zinc-200 bg-white px-3 py-2.5">
                                        <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{k}</dt>
                                        <dd className="mt-0.5 text-sm font-bold text-zinc-900">{v}</dd>
                                    </div>
                                ))}
                            </dl>
                            <ol className="mt-6 grid gap-3 sm:grid-cols-3" data-testid="dtf-steps">
                                {STEPS.map((s, i) => (
                                    <li key={s.title} className="rounded-xl border border-zinc-200 bg-white p-4">
                                        <span className="grid h-8 w-8 place-items-center rounded-full" style={{ background: theme.primary, color: theme.on || "#fff" }}><s.icon size={15} /></span>
                                        <p className="mt-3 text-sm font-bold text-zinc-900">{i + 1}. {s.title}</p>
                                        <p className="mt-1 text-xs leading-snug text-zinc-600">{s.text}</p>
                                    </li>
                                ))}
                            </ol>
                            <div className="mt-6 flex flex-wrap gap-2">
                                <Link to={`/shirts/dtf/gang-sheet${q}`} data-testid="dtf-build">
                                    <StoreButton theme={theme} className="px-6 py-3 text-sm"><Layers size={16} /> Build your sheet</StoreButton>
                                </Link>
                                <Link to="/shirts" data-testid="dtf-back-shirts"
                                    className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-5 py-3 text-sm font-semibold text-zinc-700 hover:border-zinc-400">
                                    <Ruler size={15} /> See the shirts to press it on
                                </Link>
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <Link to={`/shirts/dtf/gang-sheet${q}`} className="relative block aspect-[4/5] overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-100" data-testid="dtf-hero-image">
                                {data.kind?.image_url && <img src={mediaUrl(data.kind.image_url)} alt="A DTF gang sheet full of transfers" className="absolute inset-0 h-full w-full object-cover" />}
                                <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-zinc-700 shadow-sm">{data.ready_label}</span>
                            </Link>
                        </Reveal>
                    </div>
                )}
            </section>
        </Layout>
    );
}
